import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import React, {useContext, useEffect, useMemo, useState} from "react";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import SeeMore from "../../../../../components/SeeMore.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import Toggle from "../../../../../components/Toggle.jsx";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import {useRenderCurrencyValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import DataGrid2 from "../../../../../components/datagrid/DataGrid2.jsx";

const Mode = {
    Turnover: 'turnover',
    Amount: 'amount',
};

export default function ProductPerformance({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let renderCurrencyValue = useRenderCurrencyValue();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let [data, setData] = useState(null);
    let authorityConfiguration = useAuthorityConfiguration();

    let [userSettings] = useUserSettings();

    useEffect(() => {
        new ErpCalculationApi(authorityConfiguration)
            .productPerformance({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                }
            })
            .then(setData)
            .catch(e => et(e).then(toast.error));
    }, [project.id, since, until, centresAllowListed, domainsAllowListed]);

    let columns = [
        {
            name: 'productName',
            header: t('orders.product-name'),
            defaultFlex: 2,
            sortable: false,
        },
        {
            name: 'amount',
            header: t('orders.amount'),
            defaultFlex: 1,
            textAlign: 'end',
            sortable: false,
        },
        {
            name: 'turnover',
            header: t('dashboard.turnover'),
            defaultFlex: 2,
            textAlign: 'end',
            minWidth: 100,
            sortable: false,
            render: (input) => renderCurrencyValue(
                input.data.turnover,
                true,
            ),
        },
    ];

    let [mode, setMode] = useState(Mode.Amount);

    let dataSource = useMemo(() => {


        if (mode === Mode.Turnover && userSettings?.isMarketing === true) {
            return data?.topProductsByTurnoverGross ?? [];
        }

        if (mode === Mode.Turnover && userSettings?.isMarketing === false) {
            return data?.topProductsByTurnoverNet ?? [];
        }


        if (mode === Mode.Amount && userSettings?.isMarketing === true) {
            return data?.topProductsByAmountGross ?? [];
        }

        if (mode === Mode.Amount && userSettings?.isMarketing === false) {
            return data?.topProductsByAmountNet ?? [];
        }

        return [];
    }, [project.id, data, mode, userSettings, since, until, centresAllowListed, domainsAllowListed]);

    // noinspection JSUnresolvedReference
    return (
        <LoadingComponent
            loading={data === null}
            className={'col-span-12 sm:col-span-6 text-slate-600 dark:text-slate-400'}
        >
            <div
                className="comp__roundedBox inline-flex flex-col w-full rounded-sm text-sm bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700">
                <div
                    className={'px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between'}>
                    <h2 className={'font-semibold text-slate-800 dark:text-slate-100'}>
                        {t('orders.top-20-products')}
                    </h2>
                    <div className={'flex gap-2'}>
                        {t('orders.turnover')}
                        <Toggle enabled={mode === Mode.Amount} setEnabled={() => {
                            setMode(mode === Mode.Amount ? Mode.Turnover : Mode.Amount);
                        }}/>
                        {t('orders.amount')}
                    </div>
                </div>
                <div className={'p-3'}>
                    <SeeMore heightClass={'h-[223px]'} active={dataSource.length >= 5}>
                        <DataGrid2
                            columns={columns}
                            dataSource={dataSource}
                        />
                    </SeeMore>
                </div>
            </div>
        </LoadingComponent>
    );
}
